import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Bike, Truck, Train, Plane, Bus, Trash2, Plus, ShieldCheck, Loader2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export const VEHICLE_LABELS = {
  bike: "Bike / scooter",
  car: "Car",
  suv: "SUV / tempo",
  bus: "Bus (pre-booked)",
  train: "Train (pre-booked)",
  flight: "Flight (pre-booked)",
};

export const SELECTABLE_VEHICLES = ["bike", "car", "suv", "bus", "train"];
export const VEHICLE_ICON = { bike: Bike, car: Truck, suv: Truck, bus: Bus, train: Train, flight: Plane };
export const SELF_DRIVEN = new Set(["bike", "car", "suv"]);

const RC_BADGE = {
  verified: ["bg-emerald-100 text-emerald-800", ShieldCheck, "RC verified"],
  pending_review: ["bg-sky-100 text-sky-800", Clock, "RC under review"],
  failed: ["bg-rose-100 text-rose-800", AlertTriangle, "RC not verified"],
  unverified: ["bg-amber-100 text-amber-800", AlertTriangle, "RC not verified yet"],
};

export const RcBadge = ({ v }) => {
  if (!SELF_DRIVEN.has(v.type)) return <Badge className="bg-slate-100 text-slate-600">No RC needed</Badge>;
  const [cls, Icon, label] = RC_BADGE[v.rc_status] || RC_BADGE.unverified;
  return <Badge className={`${cls} inline-flex items-center gap-1`} data-testid={`rc-status-${v.id}`}><Icon className="w-3 h-3"/> {label}</Badge>;
};

const VehicleRow = ({ v, onChange }) => {
  const [busy, setBusy] = useState(false);
  const Icon = VEHICLE_ICON[v.type] || Truck;
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${v.id}/verify-rc`);
      if (r.data.rc_status === "verified") toast.success(r.data.rc_name_mismatch ? "RC verified · owner name differs, our team will review" : "RC verified");
      else if (r.data.rc_status === "pending_review") toast.info("RC sent for review");
      else toast.error(r.data.rc_message || "RC could not be verified");
      await onChange();
    } catch (e) { toast.error(apiError(e, "RC check failed")); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/vehicles/${v.id}`); await onChange(); toast.success("Removed"); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const canVerify = SELF_DRIVEN.has(v.type) && ["unverified", "failed"].includes(v.rc_status);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-3" data-testid={`vehicle-row-${v.id}`}>
      <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center shrink-0"><Icon className="w-4 h-4 text-slate-700"/></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-slate-900">{VEHICLE_LABELS[v.type]}</div>
          <RcBadge v={v}/>
        </div>
        {SELF_DRIVEN.has(v.type)
          ? <div className="text-xs text-slate-500 mt-0.5">{v.make_model} · <span className="font-mono">{v.reg_no}</span></div>
          : <div className="text-xs text-slate-500 mt-0.5">Pre-booked ticket · no vehicle check</div>}
        {v.rc_status === "failed" && v.rc_message && <div className="text-[11px] text-rose-700 mt-1">{v.rc_message}</div>}
        {canVerify && (
          <Button size="sm" data-testid={`verify-rc-${v.id}`} onClick={verify} disabled={busy} className="mt-2 rounded-full h-8">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><ShieldCheck className="w-3.5 h-3.5 mr-1"/> Verify RC</>}
          </Button>
        )}
      </div>
      <button onClick={remove} disabled={busy} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
              data-testid={`remove-vehicle-${v.id}`} aria-label="Remove vehicle"><Trash2 className="w-4 h-4"/></button>
    </div>
  );
};

export const AddVehicle = ({ type, onAdded }) => {
  const [reg, setReg] = useState("");
  const [model, setModel] = useState("");
  const [ins, setIns] = useState(false);
  const [busy, setBusy] = useState(false);
  const self = SELF_DRIVEN.has(type);
  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post("/vehicles", { type, reg_no: reg, make_model: model, insurance_current: ins });
      const added = r.data[r.data.length - 1];
      if (self && added?.rc_status === "unverified") await api.post(`/vehicles/${added.id}/verify-rc`).catch((e) => toast.error(apiError(e, "RC check failed")));
      setReg(""); setModel(""); setIns(false);
      await onAdded?.();
    } catch (e) { toast.error(apiError(e, "Couldn't add vehicle")); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="vehicle-add-card">
      {self ? (
        <>
          <div>
            <label className="text-xs text-slate-500">Registration (RC) number</label>
            <Input data-testid="new-vehicle-reg" value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="e.g. PB11AB1234" className="mt-1 rounded-xl font-mono uppercase bg-white"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">Make & model</label>
            <Input data-testid="new-vehicle-model" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Maruti Swift" className="mt-1 rounded-xl bg-white"/>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-3 py-2">
            <div className="text-sm font-medium">Insurance is current</div>
            <Switch data-testid="new-vehicle-insurance" checked={ins} onCheckedChange={setIns}/>
          </div>
        </>
      ) : <div className="text-sm text-slate-600">You travel on pre-booked tickets — no vehicle or RC needed.</div>}
      <Button data-testid="add-vehicle-btn" onClick={add} disabled={busy || (self && (!reg.trim() || !model.trim()))} className="w-full rounded-full inline-flex items-center gap-1.5">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Plus className="w-4 h-4"/> {self ? "Save & verify RC" : `Add ${VEHICLE_LABELS[type]}`}</>}
      </Button>
    </div>
  );
};

const TypePicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="transport-type-picker">
    {SELECTABLE_VEHICLES.map((t) => {
      const Icon = VEHICLE_ICON[t];
      return (
        <button key={t} type="button" onClick={() => onChange(t)} data-testid={`transport-type-${t}`} aria-pressed={value === t}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${value === t ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700"}`}>
          <Icon className="w-3.5 h-3.5"/> {VEHICLE_LABELS[t].replace(" (pre-booked)", "")}
        </button>
      );
    })}
  </div>
);
export { TypePicker };

const VehicleManager = ({ compact = false }) => {
  const { user, refresh } = useAuth();
  const [type, setType] = useState(null);
  const vehicles = user?.vehicles || [];
  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="vehicle-manager">
      <div className="space-y-2">
        {vehicles.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500 text-center">No saved vehicles yet.</div>
        )}
        {vehicles.map((v) => <VehicleRow key={v.id} v={v} onChange={refresh}/>)}
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Add another way you travel</div>
        <TypePicker value={type} onChange={setType}/>
        {type && <AddVehicle type={type} onAdded={async () => { await refresh(); setType(null); }}/>}
      </div>
    </div>
  );
};

export default VehicleManager;
